import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button, Form } from 'react-bootstrap';
import offerRepository from '../../../services/repositories/offer.repository';
import productRepository from '../../../services/repositories/product.repository';
import '../../../styles/products.scss';

const AddOffer = ({ isEdit = false, offerId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    product_ids: [],
    discount_percentage: '',
    start_offre: '',
    end_offre: '',
    status: 'active',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchProducts();
    if (isEdit && offerId) {
      fetchOfferData();
    } else {
      // Set default dates for new offer
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      setFormData(prev => ({
        ...prev,
        start_offre: today.toISOString().split('T')[0],
        end_offre: tomorrow.toISOString().split('T')[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, offerId]);

  const fetchProducts = async () => {
    try {
      const response = await productRepository.getAll();
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchOfferData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await offerRepository.getById(offerId);
      const offer = response.data || response;
      
      if (!offer || !offer.id) {
        throw new Error('Offer data not found');
      }
      
      setFormData({
        title: offer.title || '',
        product_ids: offer.product_id ? [offer.product_id] : [],
        discount_percentage: offer.discount_percentage || '',
        start_offre: offer.start_offre ? offer.start_offre.split('T')[0] : '',
        end_offre: offer.end_offre ? offer.end_offre.split('T')[0] : '',
        status: offer.status || 'active',
      });
    } catch (err) {
      console.error('Error fetching offer:', err);
      setError(err.message || 'Error loading offer data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => {
      const currentIds = prev.product_ids || [];
      if (currentIds.includes(productId)) {
        return { ...prev, product_ids: currentIds.filter(id => id !== productId) };
      } else {
        return { ...prev, product_ids: [...currentIds, productId] };
      }
    });
  };

  const handleRemoveProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.filter(id => id !== productId)
    }));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filteredProducts = getFilteredProducts();
      
      if (filteredProducts.length === 1 && !formData.product_ids.includes(filteredProducts[0].id)) {
        handleProductToggle(filteredProducts[0].id);
        setSearchTerm('');
        setShowSuggestions(false);
      } else if (filteredProducts.length > 0 && !formData.product_ids.includes(filteredProducts[0].id)) {
        // Add first matching product
        handleProductToggle(filteredProducts[0].id);
        setSearchTerm('');
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const getFilteredProducts = () => {
    if (!searchTerm || searchTerm.length === 0) {
      return products.filter(p => !formData.product_ids.includes(p.id));
    }
    return products.filter(product => 
      !formData.product_ids.includes(product.id) &&
      (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.price.toString().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const handleProductSelect = (productId) => {
    if (!formData.product_ids.includes(productId)) {
      handleProductToggle(productId);
      setSearchTerm('');
      setShowSuggestions(false);
    }
  };

  const handleSelectAll = () => {
    if (formData.product_ids.length === products.length) {
      setFormData(prev => ({ ...prev, product_ids: [] }));
    } else {
      setFormData(prev => ({ ...prev, product_ids: products.map(p => p.id) }));
    }
  };

  const getProductImage = (product) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const storageBaseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
    
    const buildUrl = (imagePath) => {
      if (!imagePath) return null;
      if (imagePath.startsWith('http')) return imagePath;
      if (imagePath.startsWith('/storage/')) return `${storageBaseUrl}${imagePath}`;
      if (imagePath.startsWith('storage/')) return `${storageBaseUrl}/${imagePath}`;
      return `${storageBaseUrl}/storage/${imagePath}`;
    };
    
    if (product.main_image && product.main_image.image_path) {
      return buildUrl(product.main_image.image_path);
    } else if (product.images && product.images.length > 0) {
      const mainImg = product.images.find(img => img.is_main) || product.images[0];
      if (mainImg && mainImg.image_path) {
        return buildUrl(mainImg.image_path);
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (formData.product_ids.length === 0) {
        throw new Error('Veuillez sélectionner au moins un produit');
      }

      const submitData = {
        title: formData.title || '',
        product_ids: formData.product_ids,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        start_offre: formData.start_offre,
        end_offre: formData.end_offre,
        status: formData.status || 'active',
      };
      
      if (isEdit && offerId) {
        // For edit, update single offer (only first product)
        await offerRepository.update(offerId, {
          title: formData.title,
          product_id: formData.product_ids[0],
          discount_percentage: submitData.discount_percentage,
          start_offre: submitData.start_offre,
          end_offre: submitData.end_offre,
          status: submitData.status,
        });
      } else {
        // For create, create offers for all selected products
        await offerRepository.create(submitData);
      }
      
      navigate('/admin/offers/offer-list');
    } catch (err) {
      console.error('Error saving offer:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde de l\'offre');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/offers/offer-list');
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données de l'offre...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Informations de l'Offre</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row>
              {/* Title Field */}
              <Col lg={12}>
                <div className="mb-3">
                  <label className="form-label">
                    Titre de l'Offre <span className="text-muted">(affiché sur la page d'accueil)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Ex: Offre Spéciale Ramadan, Promo Été, etc."
                  />
                </div>
              </Col>
              
              <Col lg={12}>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">
                      Produits <span className="text-danger">*</span>
                    </label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAll}
                      type="button"
                    >
                      {formData.product_ids.length === products.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </Button>
                  </div>
                  
                  {/* Tags Input with Search */}
                  <div className="mb-3 position-relative">
                    <div 
                      className="form-control d-flex flex-wrap align-items-center gap-2 p-2" 
                      style={{ 
                        minHeight: '50px',
                        cursor: 'text'
                      }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget || e.target.tagName === 'INPUT') {
                          e.currentTarget.querySelector('input')?.focus();
                        }
                      }}
                    >
                      {/* Selected Products as Tags */}
                      {formData.product_ids.map(productId => {
                        const product = products.find(p => p.id === productId);
                        if (!product) return null;
                        const productImage = getProductImage(product);
                        return (
                          <div
                            key={productId}
                            className="d-flex align-items-center gap-1 bg-secondary text-white border rounded px-2 py-1"
                            style={{ fontSize: '13px', borderRadius: '4px' }}
                          >
                            {productImage && (
                              <img
                                src={productImage}
                                alt={product.name}
                                style={{ width: '20px', height: '20px', objectFit: 'cover', borderRadius: '2px' }}
                              />
                            )}
                            <span className="fw-bold">{product.name}</span>
                            <button
                              type="button"
                              className="btn-close btn-close-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveProduct(productId);
                              }}
                              style={{ fontSize: '10px', marginLeft: '4px' }}
                              aria-label="Remove"
                            />
                          </div>
                        );
                      })}
                      
                      {/* Search Input */}
                      <input
                        type="text"
                        className="border-0 flex-grow-1"
                        placeholder={formData.product_ids.length === 0 ? "Rechercher un produit... (Appuyez sur Enter pour ajouter)" : ""}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setShowSuggestions(true);
                        }}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={() => setShowSuggestions(true)}
                        style={{ 
                          outline: 'none',
                          minWidth: '200px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    {/* Search Suggestions Dropdown */}
                    {showSuggestions && searchTerm && (() => {
                      const filteredProducts = getFilteredProducts();
                      
                      if (filteredProducts.length > 0) {
                        return (
                          <div 
                            className="border rounded mt-1 bg-white shadow-sm"
                            style={{ 
                              maxHeight: '300px',
                              overflowY: 'auto',
                              position: 'absolute',
                              zIndex: 1000,
                              width: '100%'
                            }}
                          >
                            {filteredProducts.slice(0, 10).map((product) => {
                              const productImage = getProductImage(product);
                              return (
                                <div
                                  key={product.id}
                                  className="d-flex align-items-center gap-2 p-2 border-bottom cursor-pointer hover-bg-light"
                                  onClick={() => handleProductSelect(product.id)}
                                  style={{ 
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                  {productImage && (
                                    <img
                                      src={productImage}
                                      alt={product.name}
                                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                  )}
                                  <div className="flex-grow-1">
                                    <div className="fw-bold" style={{ fontSize: '14px' }}>
                                      {product.name}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '12px' }}>
                                      {product.price} DH
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {filteredProducts.length > 10 && (
                              <div className="p-2 text-center text-muted" style={{ fontSize: '12px' }}>
                                ... et {filteredProducts.length - 10} autres produits
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Click outside to close suggestions */}
                    {showSuggestions && (
                      <div 
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                        onClick={() => setShowSuggestions(false)}
                      />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="offer-discount" className="form-label">
                    Pourcentage de Réduction <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="offer-discount"
                    name="discount_percentage"
                    className="form-control"
                    placeholder="Ex: 10"
                    value={formData.discount_percentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                  <small className="text-muted">Entrez le pourcentage de réduction (0-100)</small>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="offer-status" className="form-label">
                    Statut
                  </label>
                  <select
                    className="form-control"
                    id="offer-status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="expired">Expiré</option>
                  </select>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="offer-start" className="form-label">
                    Date de Début <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="offer-start"
                    name="start_offre"
                    className="form-control"
                    value={formData.start_offre}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="offer-end" className="form-label">
                    Date de Fin <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="offer-end"
                    name="end_offre"
                    className="form-control"
                    value={formData.end_offre}
                    onChange={handleInputChange}
                    min={formData.start_offre}
                    required
                  />
                  <small className="text-muted">L'offre se terminera à 23:59:59 de cette date</small>
                </div>
              </Col>
            </Row>
            
            <div className="p-3 bg-light mb-3 rounded">
              <Row className="justify-content-end g-2">
                <Col lg={2}>
                  <Button 
                    variant="dark" 
                    className="w-100" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </Col>
                <Col lg={2}>
                  <Button 
                    variant="primary" 
                    className="w-100" 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer l\'Offre')}
                  </Button>
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AddOffer;
